import { Match } from "model/Match/Match";
import { getRoster, isForfeited } from "model/Roster/Roster";
import {
  getPointsRules,
  getSeasonRosterPointsOverride,
  SeasonPointsOverrides,
} from "model/Tournament/Segment/Season/Season";
import {
  AltSeasonPointsRules,
  getRosterSeasonPoints,
  SeasonPointsRules,
} from "model/Tournament/Segment/Season/SeasonPointRules";

export type NacSeasonRecord = {
  rosterId: string;
  points: number;
  matchPoints: (number | undefined)[];
  position: number | undefined;
};

export type NacSeasonRecordWithDisplay = {
  record: NacSeasonRecord;
  orgId: string | undefined;
  displayName: string | undefined;
};

const createNacSeasonRecord = (
  rosterId: string,
  numberOfPlaydays: number
): NacSeasonRecord => {
  return {
    rosterId,
    points: 0,
    matchPoints: Array(numberOfPlaydays).fill(undefined),
    position: undefined,
  };
};

export const calculateNacSeasonRecords = (
  seasonPoints: SeasonPointsRules | undefined,
  seasonPointsOverrides: SeasonPointsOverrides | undefined,
  altSeasonPoints: AltSeasonPointsRules[] | undefined,
  rosterIds: string[],
  matchesByPlayday: Match[][],
  numberOfPlaydays: number
) => {
  const records = new Map<string, NacSeasonRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createNacSeasonRecord(id, numberOfPlaydays));
  });

  for (var playdayI = 0; playdayI < matchesByPlayday.length; playdayI++) {
    const matches = matchesByPlayday[playdayI];
    for (var matchI = 0; matchI < matches.length; matchI++) {
      const match = matches[matchI];
      if (!match.finalPositions) {
        continue;
      }

      const pointsRules = getPointsRules(
        match.id,
        seasonPoints,
        altSeasonPoints
      );

      for (var rosterI = 0; rosterI < rosterIds.length; rosterI++) {
        const rosterId = rosterIds[rosterI];
        const matchRoster = getRoster(match.teams.rosters, rosterId);
        const rosterRecord = records.get(rosterId);
        const finalPos = match.finalPositions[rosterId];
        if (
          !matchRoster ||
          !rosterRecord ||
          (!finalPos && !isForfeited(matchRoster))
        ) {
          continue;
        }

        const overrideScore = !!seasonPointsOverrides
          ? getSeasonRosterPointsOverride(
              seasonPointsOverrides,
              match.id,
              rosterId
            )?.result
          : undefined;
        const pointsWithQual =
          overrideScore !== undefined
            ? overrideScore
            : isForfeited(matchRoster)
            ? 0
            : getRosterSeasonPoints(
                rosterId,
                pointsRules,
                match.finalPositions,
                match.scores ?? {},
                (_) => false,
                match.numWinners
              );

        const points = pointsWithQual === "Qual" ? 1000 : pointsWithQual;
        rosterRecord.matchPoints[playdayI] = points;
        rosterRecord.points += points;
      }
    }
  }

  const sortedRecords = Array.from(records.values()).sort(compareRecords);
  if (sortedRecords.length === 0) {
    return [];
  }

  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }
  return sortedRecords;
};

const compareRecords = (a: NacSeasonRecord, b: NacSeasonRecord): number => {
  return b.points - a.points;
};
